/**
 * 演示模式通用逻辑
 */
import {useEffect, useRef, useCallback, useState} from 'react';
import classNames from 'classnames';

import {DEMO_MODE_ZOOM, DEMO_MODE_PAGING, DEMO_MODE_TYPE} from '@/constants/document/doc';
import {DemoHeader} from '@/domains/document/DocHome/Document/header/DemoHeader';
import toast, {ToastType} from '@/components/Toast';
import {feLogger, enterDemoModeView} from '@/buried';
import * as store from '@/store/index';

import styles from './useDemoMode.module.scss';

interface StorageModeType{
    docGuid: string;
    demoState: string;
}

// 获取缓存的演示模式类型
const getDemoType = (docGuid: string) => {
    const setStorageMode = localStorage.getItem(DEMO_MODE_PAGING.storageName);
    const setStorageModeList = setStorageMode ? JSON.parse(setStorageMode) : [];
    const result = setStorageModeList.findIndex((item: { docGuid: string }) => item.docGuid === docGuid);

    return result === -1 ? DEMO_MODE_TYPE.flow : (setStorageModeList[result].demoState || DEMO_MODE_TYPE.flow);
};

const useDemoModeOptions = (docGuid: string, isNew: boolean, isCardView: boolean) => {
    const [zoom, setZoom] = useState(DEMO_MODE_ZOOM.default); // 演示模式缩放数值
    const [demoModeType, setDemoModeType] = useState<string>(
        isNew
            ? isCardView ? DEMO_MODE_TYPE.page : DEMO_MODE_TYPE.flow
            : getDemoType(docGuid)
    ); // 演示模式展示方式 flow：连续，paging：分页

    // 缓存演示模式类型
    const setStorageMode = (modeType: string) => {
        const setStorageMode = localStorage.getItem(DEMO_MODE_PAGING.storageName);
        const setStorageModeList = setStorageMode ? JSON.parse(setStorageMode) : [];
        const result = setStorageModeList.findIndex((item: StorageModeType) => item.docGuid === docGuid);
        if (result !== -1) {
            setStorageModeList[result].demoState = modeType;
            localStorage.setItem(DEMO_MODE_PAGING.storageName, JSON.stringify(setStorageModeList));
        } else if (setStorageModeList.length < 10) {
            setStorageModeList.unshift({docGuid: docGuid, demoState: modeType});
            localStorage.setItem(DEMO_MODE_PAGING.storageName, JSON.stringify(setStorageModeList));
        } else if (setStorageModeList.length === 10) {
            setStorageModeList.pop();
            setStorageModeList.unshift({docGuid: docGuid, demoState: modeType});
            localStorage.setItem(DEMO_MODE_PAGING.storageName, JSON.stringify(setStorageModeList));
        }
    };

    const getDemoModeConfig = (type: string, width: number, height: number) => {
        const boxWProportion = width / DEMO_MODE_PAGING.wProportion;
        const boxHProportion = height / DEMO_MODE_PAGING.hProportion;
        const wProportion = DEMO_MODE_PAGING.wProportion;
        const hProportion = DEMO_MODE_PAGING.hProportion;
        const topAndBottom = DEMO_MODE_PAGING.topProportion + DEMO_MODE_PAGING.bottomProportion;
        const leftAndRight = DEMO_MODE_PAGING.leftProportion * 2;

        if (type === 'margin') {
            if (boxWProportion > boxHProportion || boxWProportion === boxHProportion) {
                return {
                    width: `${width}px`,

                    height: `${height}px`,
                    paddingTop: `${DEMO_MODE_PAGING.topProportion}px`,
                    paddingRight: `${(width - (wProportion * (height - topAndBottom) / hProportion)) / 2}px`,
                    paddingBottom: `${DEMO_MODE_PAGING.bottomProportion}px`,
                    paddingLeft: `${(width - (wProportion * (height - topAndBottom) / hProportion)) / 2}px`,
                };

            } else {
                return {
                    width: `${width}px`,
                    height: `${height}px`,
                    paddingTop: `${(height - ((width - leftAndRight) * hProportion / wProportion)) / 2}px`,
                    paddingRight: `${DEMO_MODE_PAGING.leftProportion}px`,
                    paddingBottom: `${(height - ((width - leftAndRight) * hProportion / wProportion)) / 2}px`,
                    paddingLeft: `${DEMO_MODE_PAGING.leftProportion}px`,
                };
            }
        } else if (type === 'overflow') {
            if (boxWProportion > boxHProportion || boxWProportion === boxHProportion) {
                return {
                    // eslint-disable-next-line max-len
                    width: `${wProportion * (height - topAndBottom) / hProportion}px`,
                    // eslint-disable-next-line max-len
                    height: `${height - topAndBottom}px`,
                    overflow: 'hidden',
                };

            } else {
                return {
                    width: `${width - leftAndRight}px`,
                    height: `${(width - leftAndRight) * hProportion / wProportion}px`,
                    overflow: 'hidden',
                };
            }
        }

        return {};
    };

    return {
        zoom,
        demoModeType,
        setStorageMode,
        changeZoom: (num: number) => {
            setZoom(num);
        },
        changeDemoModeType: (modeType: string) => {
            setDemoModeType(modeType);
        },
        getDemoModeConfig,
    };
};

interface Options {
    demoMode: boolean;
    docGuid: string;
    isNew: boolean; // 是否是引入卡片视图后的新版演示模式
    isCardView: boolean; // 是否是卡片视图
    closeDemoMode: () => void;
}

export default ({demoMode, docGuid, isNew, isCardView, closeDemoMode}: Options) => {
    // 演示模式当前页码数
    const [pageNum, setPageNum] = useState<number>(DEMO_MODE_PAGING.pagingStart);
    // 演示模式页码总数
    const [pageTotal, setPageTotal] = useState<number>(DEMO_MODE_PAGING.pagingStart);
    // 处理进入演示模式分页 键盘事件执行拿不到页码总数
    const pageTotalKey = useRef(DEMO_MODE_PAGING.pagingStart);
    // 演示模式分页宽高
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);

    const rootRef = useRef<HTMLDivElement>(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);

    // 进入演示模式宽高
    function updateWidth() {
        setWidth(window.screen.width);
        setHeight(window.screen.height);
    }

    useEffect(() => {
        const body = document.body;
        if (demoMode) {
            // @hack 解决safari 上演示模式无法正常滚动 需要同时在知识库页面和 RTE 页面的 body 元素上增加 overflow:hidden
            body.style.overflow = 'hidden';
            updateWidth();
        } else {
            body.style.overflow = 'auto';
            setWidth(0);
            setHeight(0);
        }
    }, [demoMode]);

    const {
        zoom,
        changeZoom,
        demoModeType,
        changeDemoModeType,
        setStorageMode,
        getDemoModeConfig,
    } = useDemoModeOptions(docGuid, isNew, isCardView);

    useEffect(() => {
        if (isNew) {
            // 默认选中类型，与视图有关
            if (!demoMode) {
                changeDemoModeType(isCardView ? DEMO_MODE_TYPE.page : DEMO_MODE_TYPE.flow);
            }

            // 首次访问逻辑
            if (demoMode) {
                feLogger(enterDemoModeView({
                    type: isCardView ? 'card' : 'doc',
                    docId: store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '',
                }));

            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demoMode, isNew, isCardView]);

    // 退出全屏
    function handleEsc() {
        changeZoom(DEMO_MODE_ZOOM.default);
        setPageNum(DEMO_MODE_PAGING.pagingStart);
        setPageTotal(DEMO_MODE_PAGING.pagingStart);
        closeDemoMode();

    }
    // 演示模式缩放
    function handleChangeZoom(type: string) {
        if (type === 'reduce') {
            if (zoom > DEMO_MODE_ZOOM.reduce) {
                changeZoom((zoom * 10 - 0.1 * 10) / 10);
            }
        }
        if (type === 'enlarge') {
            if (zoom < DEMO_MODE_ZOOM.enlarge) {
                changeZoom((zoom * 10 + 0.1 * 10) / 10);
            }
        }
    }
    // 演示模式切换展示方式
    function handleChangeType(modeType: string) {
        // modeType为 flow：连续，paging：分页
        setPageNum(DEMO_MODE_PAGING.pagingStart);
        changeDemoModeType(modeType);
        setStorageMode(modeType);
    }

    // 演示模式分页页码切换
    function handleChangePaging(type: string) {
        // 正在进行图片预览时优先预览器的操作，此时禁止翻页
        if (document.body.classList.contains('viewer-open')) {
            return;
        }
        if (type === 'prev') {
            if (pageNum > DEMO_MODE_PAGING.pagingStart) {
                setPageNum(pageNum - 1);
            }
        }
        if (type === 'next') {
            if (pageNum < pageTotalKey.current) {
                setPageNum(pageNum + 1);
            }
        }
    }

    // 鼠标or触控板单击演示⻚面翻页
    const nextPaging = (event: any) => {
        const isSlipSelection = window.getSelection();
        if (
            demoMode
            && demoModeType === DEMO_MODE_TYPE.page
            && !event.withMorphoBehavior
            // 鼠标未进行滑选进行翻页
            && isSlipSelection?.type !== 'Range') {
            handleChangePaging('next');
        }
    };

    // 演示模式获取总页数
    const onPageTotalChanged = useCallback((total: number) => {
        setPageTotal(total);
        pageTotalKey.current = total;
    }, []);

    // 当前页数
    const onPageChange = useCallback((current: number) => {
        setPageNum(current);
    }, [setPageNum]);

    useEffect(() => {
        const selection = window.getSelection();
        // 页码变化，取消滑选内容（解决键盘快捷键翻页不取消滑选问题）
        if (!selection?.isCollapsed) {
            selection?.removeAllRanges();
        }
    }, [pageNum]);

    function renderLayoutNode() {
        if (demoMode) {
            return (
                <div
                    ref={rootRef}
                    className={
                        classNames(
                            styles.wrap,
                            demoModeType === DEMO_MODE_TYPE.page ? styles.demoModePage : styles.demoModeFlow
                        )
                    }
                    style={
                        demoModeType === DEMO_MODE_TYPE.page && !isNew
                            ? getDemoModeConfig('margin', width, height)
                            : {}
                    }
                >
                    <div className={demoModeType === DEMO_MODE_TYPE.page ? styles.pageModeBox : ''}>
                        {/* 工具栏迁移到编辑器 */}
                        {/* <DemoHeader
                            onChangeType={handleChangeType}
                            onChangePaging={handleChangePaging}
                            onChangeZoom={handleChangeZoom}
                            onEsc={handleEsc}
                            zoomNum={zoom}
                            demoModeType={demoModeType}
                            currentNum={pageNum}
                            totalNum={pageTotal}
                            isNew={isNew}
                            contentContainerRef={contentContainerRef}
                        /> */}

                        <div ref={contentContainerRef} />
                    </div>
                </div>
            );
        } else {
            return null;
        }
    }

    return {
        renderLayoutNode,
        demoModeType,
        rootRef,
        contentContainerRef,
        zoom,
        pageNum,
        onPageChange,
        onPageTotalChanged,
        nextPaging,
        handleEsc,
        handleChangeType,
    };
};
